import { queryOptions } from '@tanstack/react-query';

import { api } from '@shared/api';
import { createQuery } from '@shared/lib/query-helpers';

import {
  crmBtProgramLogDetailResponseSchema,
  crmBtProgramLogFlowsResponseSchema,
  crmBtProgramLogListResponseSchema,
  crmBtProgramLogPageSize,
  crmBtProgramLogStatusesResponseSchema,
  crmBtLevelsResponseSchema,
  crmBtProgramLogTrainingProgramsPageSchema,
  crmBtProgramLogTrainingProgramDetailSchema,
  type CrmBtProgramLogDetailResponse,
  type CrmBtProgramLogFlowsResponse,
  type CrmBtProgramLogFilters,
  type CrmBtProgramLogListResponse,
  type CrmBtProgramLogStatusesResponse,
  type CrmBtLevelsResponse,
  type CrmBtProgramLogTrainingProgramOptionsResponse,
  type CrmBtProgramLogTrainingProgramsResponse,
  type CrmBtProgramLogTrainingProgramDetailResponse,
} from './schemas';

const trainingProgramSelectorPageSize = 100;
const maxTrainingProgramSelectorPages = 100;

export type CrmBtProgramLogCollectionQueryParams = CrmBtProgramLogFilters & {
  cursor?: string;
  profileRef: string;
  viewerAccessScope: string;
};

export type CrmBtProgramLogDetailQueryParams = {
  programLogId: string;
  viewerAccessScope: string;
};

function canonicalValues(values: readonly string[] | undefined) {
  return values === undefined ? [] : [...new Set(values)].sort();
}

function normalizedQuery(value: string | undefined) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

export const crmBtProgramLogKeys = {
  all: ['crm-bt-program-logs'] as const,
  detail: (params: CrmBtProgramLogDetailQueryParams) =>
    [...crmBtProgramLogKeys.all, 'detail', params.viewerAccessScope, params.programLogId] as const,
  list: (params: CrmBtProgramLogCollectionQueryParams) =>
    [
      ...crmBtProgramLogKeys.all,
      'list',
      params.viewerAccessScope,
      params.profileRef,
      params.cursor ?? '',
      normalizedQuery(params.q) ?? '',
      canonicalValues(params.flows),
      canonicalValues(params.programIds),
      canonicalValues(params.statuses),
      params.startedFrom ?? '',
      params.startedTo ?? '',
      crmBtProgramLogPageSize,
    ] as const,
  statuses: (viewerAccessScope: string) =>
    [...crmBtProgramLogKeys.all, 'statuses', viewerAccessScope] as const,
  btLevels: (viewerAccessScope: string) =>
    [...crmBtProgramLogKeys.all, 'bt-levels', viewerAccessScope] as const,
  flows: (viewerAccessScope: string) =>
    [...crmBtProgramLogKeys.all, 'flows', viewerAccessScope] as const,
  trainingPrograms: (viewerAccessScope: string, status?: 'Published') =>
    [
      ...crmBtProgramLogKeys.all,
      'training-program-selector',
      viewerAccessScope,
      status ?? 'all',
    ] as const,
  trainingProgramDetail: (viewerAccessScope: string, trainingProgramId: string, locale: string) =>
    [
      ...crmBtProgramLogKeys.all,
      'training-program-detail',
      viewerAccessScope,
      trainingProgramId,
      locale,
    ] as const,
};

/** Canonical profile-scoped collection of individual BT Program Log records. */
export function crmBtProgramLogCollectionQuery(params: CrmBtProgramLogCollectionQueryParams) {
  return createQuery<CrmBtProgramLogListResponse>({
    path: `/api/v1/user-profiles/${encodeURIComponent(params.profileRef)}/bt-program-logs`,
    queryKey: crmBtProgramLogKeys.list(params),
    requestOptions: {
      params: {
        cursor: params.cursor,
        flow: canonicalValues(params.flows),
        limit: crmBtProgramLogPageSize,
        programId: canonicalValues(params.programIds),
        q: normalizedQuery(params.q),
        startedFrom: params.startedFrom,
        startedTo: params.startedTo,
        status: canonicalValues(params.statuses),
      },
    },
    schema: crmBtProgramLogListResponseSchema,
  });
}

/** Independent detail read: the server resolves and authorizes its participant relationship. */
export function crmBtProgramLogDetailQuery(params: CrmBtProgramLogDetailQueryParams) {
  return queryOptions({
    queryKey: crmBtProgramLogKeys.detail(params),
    queryFn: async ({
      signal,
    }): Promise<CrmBtProgramLogDetailResponse & { etag: string | null }> => {
      const response = await api.getWithMetadata<CrmBtProgramLogDetailResponse>(
        `/api/v1/bt-program-logs/${encodeURIComponent(params.programLogId)}`,
        { schema: crmBtProgramLogDetailResponseSchema, signal }
      );
      return { ...response.data, etag: response.headers.get('etag') };
    },
  });
}

/** Independent bounded Option Set catalogue for the stored Program Log status filter. */
export function crmBtProgramLogStatusesQuery(viewerAccessScope: string) {
  return createQuery<CrmBtProgramLogStatusesResponse>({
    path: '/api/v1/bt-program-logs/statuses',
    queryKey: crmBtProgramLogKeys.statuses(viewerAccessScope),
    schema: crmBtProgramLogStatusesResponseSchema,
  });
}

/** Shared canonical BT Level options; Program detail owns compatibility only. */
export function crmBtLevelsQuery(viewerAccessScope: string) {
  return createQuery<CrmBtLevelsResponse>({
    path: '/api/v1/bt-levels',
    queryKey: crmBtProgramLogKeys.btLevels(viewerAccessScope),
    schema: crmBtLevelsResponseSchema,
  });
}

/** Independent bounded Option Set catalogue for the program-owned Flow selector. */
export function crmBtProgramLogFlowsQuery(viewerAccessScope: string) {
  return createQuery<CrmBtProgramLogFlowsResponse>({
    path: '/api/v1/bt-program-logs/flows',
    queryKey: crmBtProgramLogKeys.flows(viewerAccessScope),
    schema: crmBtProgramLogFlowsResponseSchema,
  });
}

/**
 * Existing BT Program catalogue, loaded independently of the Program Log table cursor and filters.
 * It is bounded by both server page size and a defensive maximum traversal count.
 */
export function crmBtProgramLogTrainingProgramsQuery(
  viewerAccessScope: string,
  status?: 'Published'
) {
  return queryOptions({
    queryKey: crmBtProgramLogKeys.trainingPrograms(viewerAccessScope, status),
    queryFn: async ({ signal }): Promise<CrmBtProgramLogTrainingProgramOptionsResponse> => {
      const trainingPrograms: CrmBtProgramLogTrainingProgramsResponse['data']['training_programs'][number][] =
        [];
      let pageNumber = 1;

      for (let pageIndex = 0; pageIndex < maxTrainingProgramSelectorPages; pageIndex += 1) {
        signal.throwIfAborted();
        const page = await api.get('/api/v1/training-programs', {
          params: {
            page: pageNumber,
            pageSize: trainingProgramSelectorPageSize,
            ...(status ? { status } : {}),
          },
          schema: crmBtProgramLogTrainingProgramsPageSchema,
          signal,
        });
        if (
          page.meta.page !== pageNumber ||
          page.meta.pageSize !== trainingProgramSelectorPageSize
        ) {
          throw new Error('BT Program catalogue returned invalid numbered-page metadata.');
        }
        trainingPrograms.push(...page.data.training_programs);

        if (pageNumber >= page.meta.totalPages) {
          return { data: { training_programs: trainingPrograms } };
        }
        pageNumber += 1;
      }

      throw new Error('BT Program catalogue exceeded the selector page limit.');
    },
  });
}

export function crmBtProgramLogTrainingProgramDetailQuery(
  viewerAccessScope: string,
  trainingProgramId: string,
  locale: string
) {
  return createQuery<CrmBtProgramLogTrainingProgramDetailResponse>({
    path: `/api/v1/training-programs/${encodeURIComponent(trainingProgramId)}`,
    queryKey: crmBtProgramLogKeys.trainingProgramDetail(
      viewerAccessScope,
      trainingProgramId,
      locale
    ),
    requestOptions: { params: { locale } },
    schema: crmBtProgramLogTrainingProgramDetailSchema,
  });
}
